import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Generic OTP dialog. Used for both submit and view flows.
 *
 * Props:
 *   isOpen
 *   title              — heading shown to the user ("Submit Paper", "View Paper")
 *   description        — paragraph shown below the heading
 *   requestFn()        — async; triggers OTP email. Resolves with { sentTo, expiresAt } if desired.
 *   verifyFn(otp)      — async; verifies OTP. Throws on failure.
 *   onVerified()       — called after a successful verify.
 *   onClose()
 */
function OtpDialog({
  isOpen,
  title,
  description,
  requestFn,
  verifyFn,
  onVerified,
  onClose,
}) {
  const [stage, setStage] = useState('request'); // 'request' | 'enter'
  const [otp, setOtp] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setStage('request');
      setOtp('');
      setSentTo('');
      setBusy(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSendOtp = async () => {
    setBusy(true);
    try {
      const result = await requestFn();
      setSentTo(result?.sentTo || '');
      setStage('enter');
      toast.success('OTP sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setBusy(false);
    }
  };

  const handleVerify = async () => {
    if (!otp.trim()) {
      toast.error('Enter the OTP');
      return;
    }
    setBusy(true);
    try {
      await verifyFn(otp.trim());
      toast.success('OTP verified');
      onVerified?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="qpg-modal-backdrop" onClick={onClose}>
      <div className="qpg-modal" style={{ maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
        <div className="qpg-modal-header">
          <div>
            <h3>{title}</h3>
            {description && <div className="qpg-modal-sub">{description}</div>}
          </div>
          <button className="qpg-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="qpg-modal-body">
          {stage === 'request' && (
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>📧</div>
              <p style={{ color: '#616161', fontSize: 14, margin: '0 0 16px' }}>
                Click below to receive a 6-digit one-time password at your registered email.
              </p>
              <button className="btn btn-primary" onClick={handleSendOtp} disabled={busy}>
                {busy ? 'Sending...' : 'Send OTP'}
              </button>
            </div>
          )}

          {stage === 'enter' && (
            <div style={{ padding: '6px 0' }}>
              {sentTo && (
                <p className="qpg-hint" style={{ marginBottom: 12 }}>
                  OTP sent to <strong>{sentTo}</strong>. It expires in 10 minutes.
                </p>
              )}
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#616161', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Enter OTP
              </label>
              <input
                type="text"
                className="form-control"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                style={{ letterSpacing: 6, fontSize: 18, textAlign: 'center', fontFamily: 'monospace' }}
                autoFocus
              />
              <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between' }}>
                <button className="btn btn-link" onClick={handleSendOtp} disabled={busy} style={{ background: 'none', border: 'none', color: '#1a237e', cursor: 'pointer', fontSize: 13 }}>
                  Resend OTP
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="qpg-modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          {stage === 'enter' && (
            <button className="btn btn-primary" onClick={handleVerify} disabled={busy || otp.length !== 6}>
              {busy ? 'Verifying...' : 'Verify & Continue'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default OtpDialog;
